/**
 * AuthProvider - AWS Amplify Cognito Authentication Provider
 *
 * Features:
 * - AWS Amplify v6.0.0 Cognito integration
 * - Automatic token refresh (5 minutes before expiration)
 * - MFA support (TOTP)
 * - Platform-specific token storage (MMKV/localStorage)
 * - Comprehensive error handling
 * - TypeScript type safety
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  type SignInOutput,
  type AuthError,
} from 'aws-amplify/auth';
import { User, UserRole, ARWPermissions } from '@wastelinq/models';
import { tokenStorage, TokenData } from './storage';

/**
 * Authentication User (simplified from full User model)
 */
export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  userType: 'internal' | 'portal' | 'scanner';
  permissions: ARWPermissions;
  mfaEnabled: boolean;
}

/**
 * Authentication Context Value
 */
export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ requiresMFA: boolean }>;
  logout: () => Promise<void>;
  confirmMFA: (code: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  checkMFA: () => boolean;
  clearError: () => void;
}

/**
 * Authentication Context
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Custom hook to use AuthContext
 */
export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

/**
 * Amplify Configuration Interface
 */
export interface AmplifyConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId?: string;
}

/**
 * AuthProvider Props
 */
export interface AuthProviderProps {
  children: React.ReactNode;
  config: AmplifyConfig;
}

/**
 * MFA Challenge State
 */
interface MFAChallenge {
  signInOutput: SignInOutput | null;
}

/**
 * AuthProvider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children, config }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MFAChallenge>({ signInOutput: null });

  // Ref to track refresh timer
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Initialize Amplify on mount
   */
  useEffect(() => {
    try {
      const amplifyConfig: any = {
        Auth: {
          Cognito: {
            region: config.region,
            userPoolId: config.userPoolId,
            userPoolClientId: config.userPoolWebClientId,
            loginWith: {
              email: true,
            },
          },
        },
      };

      // Add identityPoolId only if provided
      if (config.identityPoolId) {
        amplifyConfig.Auth.Cognito.identityPoolId = config.identityPoolId;
      }

      Amplify.configure(amplifyConfig);
    } catch (err) {
      console.error('Amplify configuration error:', err);
      setError('Failed to initialize authentication');
      setIsLoading(false);
    }
  }, [config]);

  /**
   * Parse User from Cognito Attributes
   */
  const parseUserFromCognito = useCallback(
    async (cognitoUser: any): Promise<AuthUser> => {
      try {
        // Get user attributes from Cognito
        const attributes = cognitoUser.signInDetails?.loginId || cognitoUser.username;
        const email = attributes;

        // In production, fetch full user profile from API using user ID
        // For now, return minimal user object
        return {
          id: cognitoUser.userId || cognitoUser.username,
          email: email,
          firstName: 'User', // TODO: Fetch from API
          lastName: 'Name', // TODO: Fetch from API
          role: 'portal_standard', // TODO: Fetch from API based on Cognito groups
          userType: 'portal', // TODO: Determine from Cognito groups
          permissions: {}, // TODO: Fetch from API
          mfaEnabled: false, // TODO: Check MFA status
        };
      } catch (err) {
        console.error('Error parsing user from Cognito:', err);
        throw new Error('Failed to parse user data');
      }
    },
    []
  );

  /**
   * Store tokens securely
   */
  const storeTokens = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const tokens = session.tokens;

      if (!tokens || !tokens.accessToken || !tokens.idToken) {
        throw new Error('No tokens available in session');
      }

      // Calculate expiration time
      const expiresIn = (tokens.accessToken.payload.exp || 0) * 1000; // Convert to milliseconds
      const expiresAt = expiresIn;

      const tokenData: TokenData = {
        accessToken: tokens.accessToken.toString(),
        refreshToken: tokens.accessToken.toString(), // Amplify handles refresh internally
        idToken: tokens.idToken?.toString() || '',
        expiresAt: expiresAt,
      };

      tokenStorage.saveTokens(tokenData);

      // Schedule token refresh (5 minutes before expiration)
      scheduleTokenRefresh(expiresAt);
    } catch (err) {
      console.error('Failed to store tokens:', err);
      throw new Error('Token storage failed');
    }
  }, []);

  /**
   * Schedule automatic token refresh
   */
  const scheduleTokenRefresh = useCallback((expiresAt: number) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Calculate time until refresh (5 minutes before expiration)
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
    const timeUntilRefresh = expiresAt - Date.now() - refreshBuffer;

    if (timeUntilRefresh > 0) {
      refreshTimerRef.current = setTimeout(async () => {
        try {
          await refreshToken();
        } catch (err) {
          console.error('Auto token refresh failed:', err);
          // If refresh fails, log user out
          await logout();
        }
      }, timeUntilRefresh);
    }
  }, []);

  /**
   * Refresh authentication token
   */
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch new session (Amplify handles refresh internally)
      const session = await fetchAuthSession({ forceRefresh: true });

      if (!session.tokens) {
        throw new Error('Failed to refresh tokens');
      }

      // Store new tokens
      await storeTokens();

      setIsLoading(false);
    } catch (err) {
      console.error('Token refresh error:', err);
      setError('Failed to refresh authentication');
      setIsLoading(false);
      throw err;
    }
  }, [storeTokens]);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string): Promise<{ requiresMFA: boolean }> => {
      try {
        setIsLoading(true);
        setError(null);

        const signInOutput = await signIn({
          username: email.toLowerCase().trim(),
          password,
        });

        // Check if MFA is required
        if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setMfaChallenge({ signInOutput });
          setIsLoading(false);
          return { requiresMFA: true };
        }

        // Sign in successful without MFA
        if (signInOutput.isSignedIn) {
          const cognitoUser = await getCurrentUser();
          const authUser = await parseUserFromCognito(cognitoUser);

          await storeTokens();
          setUser(authUser);
          setIsLoading(false);

          return { requiresMFA: false };
        }

        throw new Error('Unexpected sign in state');
      } catch (err) {
        const error = err as AuthError;
        console.error('Login error:', error);

        let errorMessage = 'Login failed';

        if (error.name === 'NotAuthorizedException') {
          errorMessage = 'Invalid email or password';
        } else if (error.name === 'UserNotFoundException') {
          errorMessage = 'User not found';
        } else if (error.name === 'UserNotConfirmedException') {
          errorMessage = 'User account not confirmed';
        } else if (error.name === 'NetworkError') {
          errorMessage = 'Network error. Please check your connection';
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }
    },
    [parseUserFromCognito, storeTokens]
  );

  /**
   * Confirm MFA code
   */
  const confirmMFA = useCallback(
    async (code: string): Promise<void> => {
      try {
        if (!mfaChallenge.signInOutput) {
          throw new Error('No MFA challenge in progress');
        }

        setIsLoading(true);
        setError(null);

        const confirmOutput = await confirmSignIn({
          challengeResponse: code.trim(),
        });

        if (confirmOutput.isSignedIn) {
          const cognitoUser = await getCurrentUser();
          const authUser = await parseUserFromCognito(cognitoUser);

          await storeTokens();
          setUser(authUser);
          setMfaChallenge({ signInOutput: null });
          setIsLoading(false);
        } else {
          throw new Error('MFA verification failed');
        }
      } catch (err) {
        const error = err as AuthError;
        console.error('MFA confirmation error:', error);

        let errorMessage = 'MFA verification failed';

        if (error.name === 'CodeMismatchException') {
          errorMessage = 'Invalid verification code';
        } else if (error.name === 'ExpiredCodeException') {
          errorMessage = 'Verification code expired';
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }
    },
    [mfaChallenge, parseUserFromCognito, storeTokens]
  );

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      // Sign out from Amplify
      await signOut();

      // Clear stored tokens
      tokenStorage.clearTokens();

      // Clear user state
      setUser(null);
      setMfaChallenge({ signInOutput: null });
      setIsLoading(false);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Logout failed');
      setIsLoading(false);
      throw err;
    }
  }, []);

  /**
   * Check if user has MFA enabled
   */
  const checkMFA = useCallback((): boolean => {
    return user?.mfaEnabled || false;
  }, [user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check existing session on mount
   */
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        setIsLoading(true);

        // Check if we have stored tokens
        const storedTokens = tokenStorage.getTokens();

        if (!storedTokens || tokenStorage.areTokensExpired()) {
          setIsLoading(false);
          return;
        }

        // Try to get current user
        const cognitoUser = await getCurrentUser();
        const authUser = await parseUserFromCognito(cognitoUser);

        // Verify session is still valid
        await fetchAuthSession();

        setUser(authUser);

        // Schedule token refresh
        if (storedTokens.expiresAt) {
          scheduleTokenRefresh(storedTokens.expiresAt);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Session check error:', err);
        // Clear invalid tokens
        tokenStorage.clearTokens();
        setIsLoading(false);
      }
    };

    checkExistingSession();

    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [parseUserFromCognito, scheduleTokenRefresh]);

  /**
   * Context value
   */
  const contextValue: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    confirmMFA,
    refreshToken,
    checkMFA,
    clearError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
